// Iter 62am — Bouton "Ajouter à l'écran d'accueil" (PWA install helper)
// Sur Android/Desktop → utilise `beforeinstallprompt` (natif).
// Sur iOS Safari → affiche une modal avec les étapes (Partager → Sur l'écran d'accueil).
// Se cache automatiquement si l'app tourne déjà en mode standalone.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Share as Share2, X, Plus } from './icons';

const isStandaloneMode = () =>
  (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches) ||
  window.navigator?.standalone === true;

const isIOS = () =>
  typeof navigator !== 'undefined' && /iP(hone|od|ad)/.test(navigator.platform || navigator.userAgent || '');

export function PWAInstallButton({ label = 'Ajouter à l\u2019écran d\u2019accueil', target = '/widget/calendar' }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosModal, setShowIosModal] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (isStandaloneMode()) { setHidden(true); return; }
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  if (hidden) return null;

  const handleClick = async () => {
    // If a native prompt is queued (Android/Desktop Chrome), use it.
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        const choice = await deferredPrompt.userChoice;
        if (choice.outcome === 'accepted') setHidden(true);
      } catch (e) { /* ignore */ }
      setDeferredPrompt(null);
      return;
    }
    // Otherwise show the iOS instructions modal (also works as fallback for
    // any browser that doesn't support the native prompt).
    setShowIosModal(true);
  };

  return (
    <>
      <button
        data-testid="pwa-install-button"
        onClick={handleClick}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '10px 16px', borderRadius: 999,
          background:
            'linear-gradient(140deg, rgba(245,197,106,0.20) 0%, rgba(245,197,106,0.06) 100%)',
          border: '1px solid rgba(245,197,106,0.45)',
          color: '#F5C56A',
          fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          boxShadow: '0 6px 18px rgba(245,197,106,0.20)',
          transition: 'all 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 8px 22px rgba(245,197,106,0.32)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 6px 18px rgba(245,197,106,0.20)';
        }}
      >
        <Plus size={13} strokeWidth={2.4} />
        {label}
      </button>

      {showIosModal && (
        <IosInstallModal target={target} onClose={() => setShowIosModal(false)} />
      )}
    </>
  );
}

function IosInstallModal({ target, onClose }) {
  const ios = isIOS();
  const url = typeof window !== 'undefined'
    ? `${window.location.origin}${target}`
    : target;
  const modalNode = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(3,7,18,0.72)',
        backdropFilter: 'blur(8px)',
        display: 'grid', placeItems: 'center', padding: 20,
        width: '100vw', height: '100vh',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="ios-install-modal"
        style={{
          width: '100%', maxWidth: 440,
          background: 'var(--sf)',
          borderRadius: 20,
          border: '1px solid rgba(245,197,106,0.35)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 22px',
          background:
            'linear-gradient(140deg, rgba(245,197,106,0.14) 0%, rgba(229,176,77,0.04) 100%)',
          borderBottom: '1px solid rgba(245,197,106,0.24)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span
            aria-hidden
            style={{
              width: 40, height: 40, borderRadius: 12,
              display: 'grid', placeItems: 'center',
              background:
                'linear-gradient(140deg, #F5C56A 0%, rgba(255,255,255,0.15) 50%, #F5C56A 100%)',
              color: '#0B1220',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55), 0 6px 14px rgba(245,197,106,0.4)',
            }}
          >
            <Share2 size={16} strokeWidth={2.4} />
          </span>
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5,
              color: '#F5C56A', letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}>Widget iPhone</div>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 17,
              color: 'var(--t1)', marginTop: 2, letterSpacing: '-0.01em',
            }}>Ajouter à l&rsquo;écran d&rsquo;accueil</div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 30, height: 30, borderRadius: 8,
              background: 'var(--sf2)',
              border: '1px solid var(--bd)',
              color: 'var(--t2)',
              cursor: 'pointer',
              display: 'grid', placeItems: 'center',
            }}
          ><X size={13} /></button>
        </div>

        {/* Body — Steps */}
        <div style={{ padding: '18px 22px' }}>
          {!ios && (
            <div style={{
              padding: '10px 12px', marginBottom: 14,
              borderRadius: 10,
              background: 'rgba(255,171,71,0.10)',
              border: '1px solid rgba(255,171,71,0.30)',
              fontFamily: 'Sora', fontWeight: 600, fontSize: 11.5,
              color: '#FFAB47',
            }}>
              ⚠️ Ouvre cette page dans Safari sur iPhone pour activer le widget.
            </div>
          )}
          <ol style={{
            paddingLeft: 0, listStyle: 'none', margin: 0,
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            <Step n={1}>Ouvre <strong>Safari</strong> sur iPhone (pas Chrome)</Step>
            <Step n={2}>Va sur <code style={{
              background: 'var(--sf2)', padding: '2px 6px', borderRadius: 4,
              fontFamily: 'monospace', fontSize: 11, color: '#5BBEFC',
              wordBreak: 'break-all',
            }}>{url}</code></Step>
            <Step n={3}>Tape l&rsquo;icône <strong>Partager</strong>&nbsp;
              <span aria-hidden style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '2px 6px', borderRadius: 5,
                background: 'rgba(91,190,252,0.15)',
                border: '1px solid rgba(91,190,252,0.35)',
                color: '#5BBEFC', fontSize: 10, fontFamily: 'Sora', fontWeight: 700,
                verticalAlign: 'middle',
              }}><Share2 size={10} /></span> en bas de Safari</Step>
            <Step n={4}>Scroll et tape <strong>&laquo;&nbsp;Sur l&rsquo;écran d&rsquo;accueil&nbsp;&raquo;</strong></Step>
            <Step n={5}>Valide <strong>&laquo;&nbsp;Ajouter&nbsp;&raquo;</strong> en haut à droite</Step>
          </ol>
          <button
            onClick={() => {
              if (navigator.clipboard) {
                navigator.clipboard.writeText(url).catch(() => {});
              }
            }}
            data-testid="pwa-copy-url"
            style={{
              marginTop: 14, width: '100%',
              padding: '10px 12px', borderRadius: 10,
              background: 'var(--sf2)',
              border: '1px solid var(--bd)',
              color: 'var(--t2)',
              fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
              cursor: 'pointer',
            }}
          >Copier l&rsquo;URL du widget</button>
        </div>
      </div>
    </div>
  );
  // Iter 62bf — Portal into <body> to escape transformed ancestors (mobile
  // dashboard carousel uses `transform: translate3d`, which was breaking the
  // modal's `position: fixed` — it rendered as a narrow column trapped
  // inside a carousel slide. createPortal fixes that.
  if (typeof document === 'undefined') return modalNode;
  return createPortal(modalNode, document.body);
}

function Step({ n, children }) {
  return (
    <li style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      fontFamily: 'Sora', fontWeight: 600, fontSize: 13,
      color: 'var(--t1)', lineHeight: 1.5,
    }}>
      <span
        aria-hidden
        style={{
          width: 22, height: 22, borderRadius: 6,
          background: 'rgba(245,197,106,0.14)',
          border: '1px solid rgba(245,197,106,0.35)',
          color: '#F5C56A',
          display: 'grid', placeItems: 'center',
          fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
          flexShrink: 0, marginTop: 1,
        }}
      >{n}</span>
      <div>{children}</div>
    </li>
  );
}
